import React, { forwardRef, useImperativeHandle, useRef, useState, useEffect, useMemo } from 'react'
import { FINDING_ACTIONS, FINDING_STATUS, FINDING_UPDATE_TASK_TYPES, SIMILAR_FINDINGS_TAGS_OPERTAION_TYPES, TOOLS_NOT_FOR_FUTURE_FINDINGS, SIMILAR_FINDINGS_TYPES } from './Constants';
import { CellRenderers } from "utils/tableUtils";
import { Form, Spin, Tooltip } from 'antd';
import { FieldTypes, generateFormFields } from 'utils/formUtils';
import BaseModal from 'components/BaseModal';
import FindingAPI from 'restAPI/FindingAPI';
import Vocabulary from 'components/Vocabulary';
import AppLoader from 'components/AppLoader';
import { _, findIndex, isEmpty, lowerCase, noop } from 'lodash';
import { hasPermission } from 'utils/authService';
import { PermissionSetMap } from 'constants/RBACConstants';
import { MarkdownFormatter } from "components/MarkupFormatter";
import message from 'components/CustomMessage';
import { MODAL_SIZE_ULTRA } from 'constants/Layout';
import SimilarFindingsTable from './SimilarFindingsTable';
import { useFindingScopeFields } from 'views/Findings/utils';
import ModalUtil from 'utils/modalUtil';
import { OnError } from "./AcceptRiskPopup";


const FalsePositivePopup = forwardRef(({finding, afterClose, action, onOk, history, featureFlags}, ref) => {
    const thisRef = useRef();
    useImperativeHandle(ref, () => ({ ...thisRef.current }));
    const [form] = Form.useForm();
    const [similarFindings, setSimilarFindings] = useState(0);
    const [loading, setLoading] = useState(false);
    const [scope, setScope] = useState('FINDING');
    const [rule, setRule] = useState(undefined);
    const [showFindingsTable, setShowFindingsTable] = useState(false);
    const similarFindingsToNotUpdate = useRef([]);
    const [updateType, setUpdateType] = useState(SIMILAR_FINDINGS_TYPES.RULE_BASED);
    const [tagCondition, setTagCondition] = useState('EQUALS');

    const findingUpdateTaskType = action === FINDING_ACTIONS.FALSEPOSITIVE ? FINDING_UPDATE_TASK_TYPES.FALSE_POSITIVE : FINDING_UPDATE_TASK_TYPES.OPEN;

    useEffect(() => {
        if(!scope || scope === 'FINDING' || !similarFindings){
            setRule(undefined);
            return noop;
        }
        const { promise, cancel } = FindingAPI.similarFindings.getRule({ 
            findingId: finding.id,
            taskType: action === FINDING_ACTIONS.FALSEPOSITIVE ? 'false_positive' : 'open',
            scope,
        });
        promise.then(setRule);
        return cancel;
    }, [finding.id, scope, action, similarFindings]);

    useEffect(() => {
        if(!isEmpty(rule)) {
            form.setFieldsValue({
                applyOnFutureFindings: false
            });
        }
    },[rule, form]);

    const handleOnOk = async () => {
        await form.validateFields();
        try {
            const data = form.getFieldsValue();
            const response = {
                ...data,
                id: finding.id, 
                findingId: finding.id,
                scope,
                updateType,
                contains: tagCondition === SIMILAR_FINDINGS_TAGS_OPERTAION_TYPES.contains,
                equals: tagCondition === SIMILAR_FINDINGS_TAGS_OPERTAION_TYPES.equals,
                status: action === FINDING_ACTIONS.FALSEPOSITIVE ? FINDING_STATUS.FALSE_POSITIVE : FINDING_STATUS.OPEN,
                ...(!_.isEmpty(similarFindingsToNotUpdate.current) ? { ignoreFindingIds: similarFindingsToNotUpdate.current } : {})
            }
            const { errorMessage, exceptionType } = await onOk(response, similarFindings, similarFindingsToNotUpdate.current);
            if(errorMessage){
                ModalUtil.show({
                    content: (
                        <OnError
                            message={errorMessage}
                            response={{
                                ...response,
                                toolName: finding.toolName,
                                productName: finding.product.name,
                                subProductName: finding.subProduct.name,
                                envName: finding.envName,
                                exceptionType,
                            }}
                        />
                    ),
                    afterClose,
                })
            }
        } catch (e) {
            console.error(e)
        }
    }
        
    const scopeOnChange = (_scope) => {
        setScope(_scope);
        if (_scope === 'FINDING') {
            setShowFindingsTable(false);
            setSimilarFindings(0);
        }
    };

    const payload = useMemo(() => ({
        scope: scope,
        updateType,
        contains: tagCondition === SIMILAR_FINDINGS_TAGS_OPERTAION_TYPES.contains,
        equals: tagCondition === SIMILAR_FINDINGS_TAGS_OPERTAION_TYPES.equals,
        findingId: finding.id,
        findingUpdateTaskType,
    }), [finding, scope, tagCondition, updateType, findingUpdateTaskType])

    useEffect(() => {
        if (scope !== 'FINDING') {
            setLoading(true);
            
            const { promise, cancel } = FindingAPI.similarFindings.count(payload);

            promise.then(response => setSimilarFindings(response || 0)).catch((e) => {
                console.error(e);
                if(e.response.data.message.startsWith("Simila")){
                    message.error("Similar Finding Rules cannot be applied to this finding")
                    setSimilarFindings(undefined)
                }
            }).finally(() => setLoading(false));

            return cancel;
        }
    }, [scope, payload]);
    
    const redirectToBucket = () => {
        setShowFindingsTable(true);
    };

    const permission = hasPermission(action === FINDING_ACTIONS.FALSEPOSITIVE ? PermissionSetMap.SIMILAR_FINDINGS_FALSEPOSITIVE : PermissionSetMap.SIMILAR_FINDINGS_REOPEN);
    
    const scopeParams = useMemo(() => ({
        finding,
        helpText: !permission ? <>Similar finding {action === FINDING_ACTIONS.FALSEPOSITIVE ? <Vocabulary name = "falsePositive"/> : <>Re-<Vocabulary name = "open"/></>} permission is required to change scope.</> : '',
        permission,
        scopeOnChange,
        redirectToBucket,
        similarFindings,
        showFindingsTable,
        scope,
        updateType,
        setUpdateType,
        setTagCondition
    }), [finding, permission, similarFindings, showFindingsTable, scope, updateType, action])

    const scopeFields = useFindingScopeFields({payload, ...scopeParams});

    const fields = useMemo(() => {
        const fields = [
            {
                name: "summary",
                label: "Summary",
                type: FieldTypes.LABEL,
                value: <MarkdownFormatter markdown={finding.title} collapsible={true} />,
            },
            {
                name: "note",
                label: "Note",
                type: FieldTypes.TEXT_AREA,
                required: true,
                customLengthRule: {
                    max: 1000,
                    message: "Maximum length is 1000 characters"
                },
                props: {
                    autoFocus: true
                }
            },
        ]

        if(featureFlags.VERSIONS){
            fields.push({
                label: "Apply to all unresolved versioned Findings",
                name: "applyToVersionFindings",
                type: FieldTypes.SWITCH,
                inline: true
            })
        }

        fields.push(...scopeFields);

        const scopeIndex =  findIndex(fields, { name: "scope" });

        if (action === FINDING_ACTIONS.FALSEPOSITIVE && scopeIndex !== -1 && scope !== 'FINDING' && !_.isUndefined(similarFindings) && TOOLS_NOT_FOR_FUTURE_FINDINGS.indexOf(_.toLower(finding.toolName || finding.source)) === -1) {
            if (updateType === SIMILAR_FINDINGS_TYPES.RULE_BASED) {
                fields.splice(scopeIndex + 1, 0, {
                    name: "applyOnFutureFindings",
                    label: <Tooltip title={!isEmpty(rule) ?
                        `Rule already exists to mark all future findings as ${lowerCase(rule.otherProperties.ACTION_STATUS)}.`
                        : undefined}>Apply to all future findings</Tooltip>,
                    type: FieldTypes.BOOLEAN,
                    disabled: !isEmpty(rule),
                    permission: PermissionSetMap.SIMILAR_FINDINGS_RULE_UPDATE,
                    inline: true,
                });
            }
        }

        const tableprops = {
            title: `${similarFindings} Similar Findings`,
            similarFindingsToNotUpdate,
            scope,
            findingUpdateTaskType,
            sourceFinding: finding.id,
            updateType,
            tagCondition,
        };

        if (showFindingsTable) {
            fields.push({
                name: "findingsTable",
                label: '',
                render: () => <SimilarFindingsTable {...tableprops} />
            });
        }

        return fields;
    }, [action, finding, findingUpdateTaskType, rule, scope, scopeFields, showFindingsTable, similarFindings, tagCondition, updateType, featureFlags.VERSIONS])
    
    const modalProps = {
        title: <>{finding?.status === FINDING_STATUS.FALSE_POSITIVE ? <>Re-<Vocabulary name = "open"/></> : <>Mark {finding?.id} as <Vocabulary name="falsePositive" /></>} {CellRenderers.CUSTOMIZED_SEVERITY(finding.severity)} </>,
        onOk: handleOnOk,
        okText: "Submit",
        okButtonProps: {
            disabled: _.isUndefined(similarFindings)
        },
        width: showFindingsTable ? MODAL_SIZE_ULTRA : undefined,
        className: showFindingsTable ? "modal-full-height no-footer" : '',
    }

    return (
        <BaseModal
            modalProps={modalProps}
            ref={thisRef}
            afterClose={afterClose}
        >
            <Spin spinning={loading} indicator={<AppLoader />}>
                <Form
                    layout="vertical"
                    form={form}
                    initialValues={{
                        scope: "FINDING",
                        applyToVersionFindings: true
                    }}
                >
                    {generateFormFields(fields)}
                </Form>
            </Spin>
        </BaseModal>
    )
})

export default FalsePositivePopup
